import cx from 'classnames';
import {get, set} from 'lodash';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {userSettingsApi} from 'api/user-settings';
import {DataWrapper, PredefinedNotifierType, Radio, Toggle, ToggleProps} from 'components';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.AGENTS_AVAILABILITY;

enum Fields {
    CheckAvailableAgents = 'checkAgentsAvailability.enabled',
    AgentAvailableInSession = 'checkAgentsAvailability.withActiveSessionAvailable',
    CheckAssignedAgentAvailable = 'checkAssignedAgentAvailability.enabled',
    AssignedAgentUnavailableAction = 'checkAssignedAgentAvailability.onUnavailableAction'
}

export enum AssignedAgentUnavailableAction {
    AutoReassign = 'auto-reassign',
    Default = 'default',
}

type State = {
    readonly checkAgentsAvailability: {
        readonly enabled: boolean;
        readonly withActiveSessionAvailable?: boolean;
    },
    readonly checkAssignedAgentAvailability: {
        readonly enabled: boolean;
        readonly onUnavailableAction?: AssignedAgentUnavailableAction;
    },
}

export const AgentsAvailability: React.FC = () => {
    const [checkAgentsAvailabilityState, setCheckAgentsAvailabilityState] = useState<State>();
    const {t} = useTranslation();

    const {baseFontClassName, boldFontClassName} = useMemo(() => ({
        boldFontClassName: 'tw-ts-h5-bold -lg:tw-ts-base-bold',
        baseFontClassName: 'tw-ts-h5 -lg:tw-ts-base',
    }), []);

    const onUnavailableAction = useMemo(() => checkAgentsAvailabilityState?.checkAssignedAgentAvailability?.onUnavailableAction,
        [checkAgentsAvailabilityState]);

    const onSuccessFetch = useCallback((data: State) => {
        setCheckAgentsAvailabilityState(data);
    }, []);

    const onErrorFetch = useCallback(() => {
        setCheckAgentsAvailabilityState(null);
    }, []);

    const {send: fetchCheckAgentsAvailability, loading: fetchLoading, initialLoading, error: fetchError} = useApiData(
        userSettingsApi.fetchAgentsAvailability, {
            onSuccess: onSuccessFetch,
            onError: onErrorFetch,
            notificationType: PredefinedNotifierType.FetchError
        });

    const {send: updateCheckAgentsAvailability, loading: updateLoading} = useApiData(
        userSettingsApi.updateAgentsAvailability, {
            onSuccess: onSuccessFetch,
            notificationType: PredefinedNotifierType.Save
        });

    const loading = useMemo(() => fetchLoading || updateLoading, [fetchLoading, updateLoading]);

    const updateCheckAgentsAvailabilityField = useCallback((field: string, value: any) => {
        updateCheckAgentsAvailability(set({}, field, value));
    },
    [updateCheckAgentsAvailability]);

    const updateAgentUnavailableAction = useCallback((action: AssignedAgentUnavailableAction) => {
        updateCheckAgentsAvailabilityField(Fields.AssignedAgentUnavailableAction, action);
    }, [updateCheckAgentsAvailabilityField]);

    const getIsToggleChecked = useCallback((field: Fields) => !!get(checkAgentsAvailabilityState, field), [checkAgentsAvailabilityState]);

    const renderToggle = useCallback((props: {label: string, bold?: boolean, field: Fields}) => {
        const fontClassName = props.bold ? boldFontClassName : baseFontClassName;
        const checked = getIsToggleChecked(props.field);
        const toggleProps: ToggleProps = {
            name: props.field,
            className: cx(fontClassName, 'tw-w-full tw-justify-between'),
            checked,
            disabled: loading,
            labelPlacement: 'right',
            label: props.label,
            onChange: () => updateCheckAgentsAvailabilityField(props.field, !checked),
        };
        return (
            <Fragment>
                <Toggle {...toggleProps} size='small' className={cx(toggleProps.className, 'lg:tw-hidden')} />
                <Toggle {...toggleProps} className={cx(toggleProps.className, '-lg:tw-hidden')} />
            </Fragment>
        );
    }, [baseFontClassName, boldFontClassName, loading, getIsToggleChecked, updateCheckAgentsAvailabilityField]);

    useEffect(() => {
        fetchCheckAgentsAvailability();
    }, [fetchCheckAgentsAvailability]);

    return (
        <div className='tw-relative'>
            <DataWrapper isLoading={!checkAgentsAvailabilityState || initialLoading} isError={!!fetchError} hideContentOnLoading>
                <div className='tw-inline-block'>
                    <div className='tw-mb-4'>
                        {renderToggle({label: t(T_KEYS.LABELS.CHECK_AGENTS), bold: true, field: Fields.CheckAvailableAgents})}
                    </div>
                    {getIsToggleChecked(Fields.CheckAvailableAgents) &&
                        <div className='tw-mb-4 tw-ml-4'>
                            {renderToggle({
                                label: t(T_KEYS.LABELS.AVAILABLE_IN_SESSION),
                                field: Fields.AgentAvailableInSession
                            })}
                        </div>}
                    <div className='tw-mb-4'>
                        {renderToggle({
                            label: t(T_KEYS.LABELS.CHECK_ASSIGNED_AGENT),
                            bold: true,
                            field: Fields.CheckAssignedAgentAvailable
                        })}
                    </div>
                    {getIsToggleChecked(Fields.CheckAssignedAgentAvailable) &&
                        <div className='tw-mb-4 tw-ml-4 tw-pr-8 -lg:tw-pr-4'>
                            <div className={cx(baseFontClassName, 'tw-text-default  tw-mb-4')}>
                                {t(T_KEYS.LABELS.ASSIGNED_AGENT_ACTION)}
                            </div>
                            <Radio
                                name='agent-unavailable-action'
                                className={cx(baseFontClassName, 'tw-w-full tw-justify-between tw-mb-4')}
                                checked={!onUnavailableAction || onUnavailableAction === AssignedAgentUnavailableAction.Default}
                                disabled={loading}
                                labelPlacement='right'
                                label={t(T_KEYS.LABELS.ASSIGNED_AGENT_ACTION_UNAVAILABLE_SCREEN)}
                                value={AssignedAgentUnavailableAction.Default}
                                onSelect={updateAgentUnavailableAction}
                            />
                            <Radio
                                name='agent-unavailable-action'
                                className={cx(baseFontClassName, 'tw-w-full tw-justify-between tw-mb-4')}
                                checked={onUnavailableAction === AssignedAgentUnavailableAction.AutoReassign}
                                disabled={loading}
                                labelPlacement='right'
                                label={t(T_KEYS.LABELS.ASSIGNED_AGENT_ACTION_REASSIGN)}
                                value={AssignedAgentUnavailableAction.AutoReassign}
                                onSelect={updateAgentUnavailableAction}
                            />
                        </div>}
                </div>
            </DataWrapper>
        </div>
    );
};